/**
 * Manual movement entry — `/inventory/movements/new`.
 *
 * One form over the closed operator command union: receipt, issue,
 * adjustment and opening balance. The service derives ledger provenance, so
 * this form never sends a source UUID, a note or any other free text — the
 * only "why" it can state is the command's closed reason code.
 *
 * Quantities and unit costs are canonicalized to the exact wire scale by
 * string padding; a value with more precision than the scale is refused
 * rather than silently rounded. The backdating refusals
 * (`occurred_at_period_closed`, `occurred_at_before_latest_movement`) are
 * explained in place with a recovery action, never dropped into a toast.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Minus, Plus } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { formatDateTime } from "@/lib/format";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  MANUAL_MOVEMENT_KINDS,
  MANUAL_REASON_CODES,
  QUANTITY_FRACTION_DIGITS,
  UNIT_COST_FRACTION_DIGITS,
  createMovement,
  type ManualMovementKind,
  type StockMovement,
  type StockMovementRequest,
} from "../api";
import { isPositiveDecimal, negateDecimalString, toScaledDecimalString } from "../decimal";
import {
  asBeforeLatestMovementProblem,
  asInsufficientStockProblem,
  asPeriodClosedProblem,
  asUomUnconvertibleProblem,
  problemOf,
  type OccurredAtBeforeLatestMovementProblem,
  type OccurredAtPeriodClosedProblem,
} from "../problems";
import { useWarehouseOptions } from "../queries";
import { BatchSelect, LocationSelect, UomSelect, VariantPicker } from "../components/pickers";
import { ScanBox } from "../components/scan-box";

/** `<input type="datetime-local">` value for a Date, in the viewer's zone. */
function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toIsoOrNull(localValue: string): string | null {
  if (localValue === "") return null;
  const parsed = new Date(localValue);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export interface MovementEntryPageProps {
  initialVariantId?: string;
  initialWarehouseId?: string;
  canReadVariants?: boolean;
  canReadUoms?: boolean;
  canReadBatches?: boolean;
  canScan?: boolean;
  /** Posted → the ledger drill-in for the position just touched. */
  onPosted?: (movement: StockMovement) => void;
  onCancel?: () => void;
}

export function MovementEntryPage({
  initialVariantId,
  initialWarehouseId,
  canReadVariants = false,
  canReadUoms = false,
  canReadBatches = false,
  canScan = false,
  onPosted,
  onCancel,
}: MovementEntryPageProps) {
  const { t, i18n } = useTranslation();

  const [kind, setKind] = useState<ManualMovementKind>("receipt");
  const [variantId, setVariantId] = useState<string | null>(initialVariantId ?? null);
  const [variantSku, setVariantSku] = useState<string | null>(null);
  const [warehouseId, setWarehouseId] = useState<string | null>(initialWarehouseId ?? null);
  const [locationId, setLocationId] = useState<string | null>(null);
  const [batchId, setBatchId] = useState<string | null>(null);
  const [uomId, setUomId] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [negative, setNegative] = useState(false);
  const [unitCost, setUnitCost] = useState("");
  const [reasonCode, setReasonCode] = useState<string>(MANUAL_REASON_CODES.receipt[0] as string);
  const [occurredAt, setOccurredAt] = useState(() => toLocalInputValue(new Date()));

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [periodClosed, setPeriodClosed] = useState<OccurredAtPeriodClosedProblem | null>(null);
  const [beforeLatest, setBeforeLatest] = useState<OccurredAtBeforeLatestMovementProblem | null>(
    null,
  );
  const [posted, setPosted] = useState<StockMovement | null>(null);

  const warehouses = useWarehouseOptions().data ?? [];
  const warehouse = warehouses.find((candidate) => candidate.id === warehouseId) ?? null;
  const binsEnabled = warehouse?.locations_enabled ?? false;

  useEffect(() => {
    // A kind switch re-scopes the closed reason list; never post a stale code.
    setReasonCode(MANUAL_REASON_CODES[kind][0] as string);
    if (kind !== "adjustment") setNegative(false);
  }, [kind]);

  const needsUnitCost = kind === "receipt" || kind === "opening_balance";
  const allowsUnitCost = needsUnitCost || kind === "adjustment";

  const clearProblems = () => {
    setFieldErrors({});
    setPeriodClosed(null);
    setBeforeLatest(null);
  };

  const mutation = useIdempotentMutation<StockMovement, StockMovementRequest>({
    mutationFn: (command, key) => createMovement(command, key),
    onSuccess: (movement) => {
      clearProblems();
      setPosted(movement);
      setQty("");
      setUnitCost("");
      toast({
        variant: "success",
        title: t("inventory.movementEntry.posted", "Movement posted"),
      });
    },
    onError: (error) => {
      const period = asPeriodClosedProblem(error);
      if (period !== null) {
        setPeriodClosed(period);
        setBeforeLatest(null);
        return;
      }
      const latest = asBeforeLatestMovementProblem(error);
      if (latest !== null) {
        setBeforeLatest(latest);
        setPeriodClosed(null);
        return;
      }
      const shortage = asInsufficientStockProblem(error);
      if (shortage !== null) {
        setFieldErrors({
          qty:
            shortage.detail ??
            t(
              "inventory.problems.insufficientStock",
              "On hand is lower than this quantity — the position may not go negative.",
            ),
        });
        return;
      }
      const uom = asUomUnconvertibleProblem(error);
      if (uom !== null) {
        setFieldErrors({
          uom_id: t(
            "inventory.problems.uomUnconvertible",
            "This unit has no conversion to the item's base unit. Add the conversion in Catalog first.",
          ),
        });
        return;
      }
      const problem = problemOf(error);
      if (problem?.errors !== undefined && problem.errors.length > 0) {
        const mapped: Record<string, string> = {};
        for (const fieldError of problem.errors) {
          mapped[fieldError.field] = fieldError.message;
        }
        setFieldErrors(mapped);
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const canSubmit =
    variantId !== null && warehouseId !== null && uomId !== null && qty.trim() !== "";

  const buildCommand = (): StockMovementRequest | null => {
    if (variantId === null || warehouseId === null || uomId === null) return null;
    const occurredIso = toIsoOrNull(occurredAt);
    if (occurredIso === null) {
      setFieldErrors({
        occurred_at: t("inventory.movementEntry.occurredAtInvalid", "Enter a valid date and time."),
      });
      return null;
    }

    const scaledQty = toScaledDecimalString(qty, QUANTITY_FRACTION_DIGITS);
    if (scaledQty === null || !isPositiveDecimal(scaledQty)) {
      setFieldErrors({
        qty: t(
          "inventory.movementEntry.qtyInvalid",
          "Enter a quantity above zero with at most 6 decimal places.",
        ),
      });
      return null;
    }

    let scaledCost: string | null = null;
    if (unitCost.trim() !== "") {
      scaledCost = toScaledDecimalString(unitCost, UNIT_COST_FRACTION_DIGITS);
      if (scaledCost === null) {
        setFieldErrors({
          unit_cost: t(
            "inventory.movementEntry.unitCostInvalid",
            "Enter a unit cost with at most 6 decimal places.",
          ),
        });
        return null;
      }
    }
    if (needsUnitCost && scaledCost === null) {
      setFieldErrors({
        unit_cost: t(
          "inventory.movementEntry.unitCostRequired",
          "A receipt has to state the unit cost it is valued at.",
        ),
      });
      return null;
    }

    const common = {
      variant_id: variantId,
      warehouse_id: warehouseId,
      uom_id: uomId,
      occurred_at: occurredIso,
      ...(binsEnabled && locationId !== null ? { location_id: locationId } : {}),
      ...(batchId === null ? {} : { batch_id: batchId }),
    };

    if (kind === "issue") {
      return {
        ...common,
        movement_type: "issue",
        qty: scaledQty,
        reason_code: reasonCode as "manual_issue" | "damage" | "shrinkage" | "internal_consumption",
      };
    }
    if (kind === "adjustment") {
      return {
        ...common,
        movement_type: "adjustment",
        // Adjustment quantity carries its own sign; the toggle IS the sign.
        qty: negative ? negateDecimalString(scaledQty) : scaledQty,
        reason_code: reasonCode as "cycle_correction" | "data_correction" | "uom_correction",
        ...(scaledCost === null ? {} : { unit_cost: scaledCost }),
      };
    }
    if (kind === "opening_balance") {
      return {
        ...common,
        movement_type: "receipt",
        qty: scaledQty,
        unit_cost: scaledCost as string,
        reason_code: reasonCode as "opening_balance" | "migration_reconciliation",
      };
    }
    return {
      ...common,
      movement_type: "receipt",
      qty: scaledQty,
      unit_cost: scaledCost as string,
      reason_code: reasonCode as "manual_receipt" | "found_stock" | "customer_return_unlinked",
    };
  };

  const submit = () => {
    clearProblems();
    const command = buildCommand();
    if (command === null) return;
    mutation.mutate(command);
  };

  const reasonOptions = useMemo(() => MANUAL_REASON_CODES[kind], [kind]);

  return (
    <div className="flex max-w-4xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-semibold">
          {t("inventory.movementEntry.title", "New stock movement")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "inventory.movementEntry.subtitle",
            "Manual postings are the exception: receiving, shipping and counting write their own movements.",
          )}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("inventory.movementEntry.kind", "What happened")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div
            role="radiogroup"
            aria-label={t("inventory.movementEntry.kind", "What happened")}
            className="flex flex-wrap gap-2"
          >
            {MANUAL_MOVEMENT_KINDS.map((value) => (
              <Button
                key={value}
                type="button"
                role="radio"
                aria-checked={kind === value}
                variant={kind === value ? "default" : "outline"}
                onClick={() => setKind(value)}
              >
                {t(`inventory.movementKind.${value}`, value)}
              </Button>
            ))}
          </div>

          <ScanBox
            id="movement-entry-scan"
            canResolve={canScan}
            label={t("inventory.movementEntry.scan", "Scan the item")}
            onResolved={(variant) => {
              setVariantId(variant.id);
              setVariantSku(variant.sku);
            }}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-variant">{t("inventory.common.sku", "SKU")}</Label>
              <VariantPicker
                id="movement-variant"
                value={variantId}
                selectedSku={variantSku}
                canRead={canReadVariants}
                onChange={(nextId, sku) => {
                  setVariantId(nextId);
                  setVariantSku(sku);
                  setBatchId(null);
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-warehouse">
                {t("inventory.common.warehouse", "Warehouse")}
              </Label>
              <Select
                value={warehouseId ?? ""}
                onValueChange={(value) => {
                  setWarehouseId(value);
                  setLocationId(null);
                }}
              >
                <SelectTrigger id="movement-warehouse">
                  <SelectValue placeholder={t("inventory.common.pickWarehouse", "Warehouse…")} />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((candidate) => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.code} — {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {binsEnabled ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="movement-location">{t("inventory.common.location", "Bin")}</Label>
                <LocationSelect
                  id="movement-location"
                  warehouseId={warehouseId}
                  value={locationId}
                  includeAll
                  allLabel={t("inventory.movementEntry.defaultBin", "Warehouse default bin")}
                  onChange={setLocationId}
                />
              </div>
            ) : null}

            {canReadBatches ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="movement-batch">{t("inventory.common.batch", "Batch")}</Label>
                <BatchSelect
                  id="movement-batch"
                  variantId={variantId}
                  warehouseId={warehouseId}
                  value={batchId}
                  canRead={canReadBatches}
                  onChange={setBatchId}
                />
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-qty">{t("inventory.common.qty", "Quantity")}</Label>
              <div className="flex items-center gap-2">
                {kind === "adjustment" ? (
                  <div
                    className="flex gap-1"
                    role="group"
                    aria-label={t("inventory.movementEntry.direction", "Direction")}
                  >
                    <Button
                      type="button"
                      size="icon"
                      variant={negative ? "outline" : "default"}
                      aria-pressed={!negative}
                      aria-label={t("inventory.movementEntry.increase", "Increase stock")}
                      onClick={() => setNegative(false)}
                    >
                      <Plus className="size-4" aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant={negative ? "default" : "outline"}
                      aria-pressed={negative}
                      aria-label={t("inventory.movementEntry.decrease", "Decrease stock")}
                      onClick={() => setNegative(true)}
                    >
                      <Minus className="size-4" aria-hidden="true" />
                    </Button>
                  </div>
                ) : null}
                <Input
                  id="movement-qty"
                  dir="ltr"
                  inputMode="decimal"
                  className="text-end"
                  value={qty}
                  aria-invalid={fieldErrors.qty !== undefined}
                  onChange={(event) => setQty(event.target.value)}
                />
              </div>
              {fieldErrors.qty !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {fieldErrors.qty}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t(
                    "inventory.movementEntry.qtyHint",
                    "Up to 6 decimal places, in the unit below.",
                  )}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-uom">{t("inventory.common.uom", "Unit")}</Label>
              <UomSelect
                id="movement-uom"
                value={uomId}
                canRead={canReadUoms}
                onChange={setUomId}
              />
              {fieldErrors.uom_id !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {fieldErrors.uom_id}
                </p>
              ) : null}
            </div>

            {allowsUnitCost ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="movement-unit-cost">
                  {t("inventory.common.unitCost", "Unit cost")}
                </Label>
                <Input
                  id="movement-unit-cost"
                  dir="ltr"
                  inputMode="decimal"
                  className="text-end"
                  value={unitCost}
                  aria-invalid={fieldErrors.unit_cost !== undefined}
                  onChange={(event) => setUnitCost(event.target.value)}
                />
                {fieldErrors.unit_cost !== undefined ? (
                  <p role="alert" className="text-sm text-destructive">
                    {fieldErrors.unit_cost}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {needsUnitCost
                      ? t(
                          "inventory.movementEntry.unitCostHint",
                          "Required — this receipt is valued at it.",
                        )
                      : t(
                          "inventory.movementEntry.unitCostOptional",
                          "Optional. Decreases are always valued at the locked moving average.",
                        )}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label>{t("inventory.common.unitCost", "Unit cost")}</Label>
                <p className="rounded-md border bg-muted/40 p-2 text-sm text-muted-foreground">
                  {t(
                    "inventory.movementEntry.valuedAtAverage",
                    "Valued server-side at the position's locked moving average.",
                  )}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-reason">{t("inventory.common.reason", "Reason")}</Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger id="movement-reason">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`inventory.reasonCode.${value}`, value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t(
                  "inventory.movementEntry.reasonHint",
                  "A closed code — the ledger accepts no free-text note.",
                )}
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="movement-occurred-at">
                {t("inventory.common.occurredAt", "Business time")}
              </Label>
              <Input
                id="movement-occurred-at"
                type="datetime-local"
                value={occurredAt}
                aria-invalid={fieldErrors.occurred_at !== undefined}
                onChange={(event) => setOccurredAt(event.target.value)}
              />
              {fieldErrors.occurred_at !== undefined ? (
                <p role="alert" className="text-sm text-destructive">
                  {fieldErrors.occurred_at}
                </p>
              ) : null}
            </div>
          </div>

          {periodClosed !== null ? (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="font-medium">
                {t("inventory.problems.periodClosedTitle", "That date is in a closed period")}
              </p>
              <p className="text-muted-foreground">
                {t(
                  "inventory.problems.periodClosedBody",
                  "Period {{period}} is {{state}}. A movement has to be dated inside an open period.",
                  {
                    period: periodClosed.period,
                    state: t(
                      `inventory.periodState.${periodClosed.period_state}`,
                      periodClosed.period_state,
                    ),
                  },
                )}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="self-start"
                onClick={() => {
                  setOccurredAt(toLocalInputValue(new Date()));
                  setPeriodClosed(null);
                }}
              >
                {t("inventory.problems.setToNow", "Use the current time")}
              </Button>
            </div>
          ) : null}

          {beforeLatest !== null ? (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm"
            >
              <p className="font-medium">
                {t(
                  "inventory.problems.beforeLatestTitle",
                  "That date is before the latest movement",
                )}
              </p>
              <p className="text-muted-foreground">
                {t(
                  "inventory.problems.beforeLatestBody",
                  "{{warehouse}} already has a movement for this item at {{latest}}. Backdating before it would rewrite cost history, which this ledger does not do.",
                  {
                    warehouse: beforeLatest.warehouse_code,
                    latest: formatDateTime(beforeLatest.latest_occurred_at, i18n.language),
                  },
                )}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="self-start"
                onClick={() => {
                  setOccurredAt(toLocalInputValue(new Date(beforeLatest.latest_occurred_at)));
                  setBeforeLatest(null);
                }}
              >
                {t("inventory.problems.useLatest", "Use the latest accepted time")}
              </Button>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              loading={mutation.isPending}
              disabled={!canSubmit || mutation.isPending}
              onClick={submit}
            >
              {t("inventory.movementEntry.submit", "Post movement")}
            </Button>
            {onCancel !== undefined ? (
              <Button type="button" variant="ghost" onClick={onCancel}>
                {t("actions.cancel")}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {posted !== null ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
            <span className="font-medium">
              {t("inventory.movementEntry.postedTitle", "Posted to the ledger")}
            </span>
            <span dir="ltr" className="font-mono text-muted-foreground">
              {posted.sku} · {posted.warehouse_code} · {posted.qty}
            </span>
            {onPosted !== undefined ? (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0"
                onClick={() => onPosted(posted)}
              >
                {t("inventory.movementEntry.viewLedger", "View it in the ledger")}
                <ArrowRight className="size-4 rtl:rotate-180" aria-hidden="true" />
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
